import { z } from "zod";
import { SYNC_VERSION } from "../constants";
import { SHA256_PATTERN } from "./media";

/** A media file a cell points at. `ext` has no dot, as in the upload query. */
export const syncMediaRefSchema = z.object({
  sha256: z.string().regex(SHA256_PATTERN),
  ext: z.string().min(1).max(8),
  size: z.number().int().nonnegative(),
});

/** One slot of the configuration, with the player-owned and operator-owned fields. */
export const syncCellSchema = z.object({
  person_id: z.uuid(),
  position: z.number().int().nonnegative(),
  active: z.boolean(),
  hotkey: z.string().nullable(),
  name: z.string(),
  number: z.number().int().nullable(),
  song_label: z.string().nullable(),
  start_ms: z.number().int().nonnegative(),
  length_s: z.number(),
  photo: syncMediaRefSchema.nullable(),
  song: syncMediaRefSchema.nullable(),
});

export const syncConfigurationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  locked: z.boolean(),
});

/**
 * Which fields each side owns. A client overwrites only the player's fields on sync and
 * never touches the operator's. The lists name the keys of a cell.
 */
export const SYNC_OWNERSHIP = {
  player: ["photo", "song", "start_ms", "length_s"],
  operator: ["name", "number", "song_label", "active", "hotkey", "position"],
} as const;

export const syncOwnershipSchema = z.object({
  player: z.array(z.string()),
  operator: z.array(z.string()),
});

export const syncManifestSchema = z.object({
  sync_version: z.literal(SYNC_VERSION),
  configuration: syncConfigurationSchema,
  /** SHA-256 hex of the canonical JSON of `configuration` and `cells`. */
  etag: z.string().regex(SHA256_PATTERN),
  cells: z.array(syncCellSchema),
  ownership: syncOwnershipSchema,
});

export type SyncMediaRef = z.infer<typeof syncMediaRefSchema>;
export type SyncCell = z.infer<typeof syncCellSchema>;
export type SyncManifest = z.infer<typeof syncManifestSchema>;
